import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';

export interface ResourcePool {
  dagrun_pm_resourcepoolid: string;
  dagrun_pm_name: string;
  dagrun_pm_description?: string | null;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_ownername?: string | null;
  dagrun_pm_isactive?: boolean | null;
  createdon?: string;
}

export interface ResourcePoolMember {
  dagrun_pm_resourcepoolmemberid: string;
  dagrun_pm_name: string;
  dagrun_pm_resourcepoolid?: string | null;
  dagrun_pm_userid?: string | null;
  dagrun_pm_username?: string | null;
  dagrun_pm_useremail?: string | null;
  dagrun_pm_role?: string | null;
  dagrun_pm_capacity?: number | null;
  dagrun_pm_defaulthoursperday?: number | null;
  dagrun_pm_defaulthourlyrate?: number | null;
  dagrun_pm_dateadded?: string | null;
  createdon?: string;
}

// Dataverse cannot hold a default value on a decimal attribute — the
// 8 h/day default is enforced here at write time instead.
export const DEFAULT_HOURS_PER_DAY = 8;

const POOL_FIELDS = [
  'dagrun_pm_resourcepoolid', 'dagrun_pm_name', 'dagrun_pm_description',
  'dagrun_pm_ownerid', 'dagrun_pm_ownername', 'dagrun_pm_isactive',
  'createdon',
];
const MEMBER_FIELDS = [
  'dagrun_pm_resourcepoolmemberid', 'dagrun_pm_name',
  'dagrun_pm_resourcepoolid', 'dagrun_pm_userid', 'dagrun_pm_username',
  'dagrun_pm_useremail', 'dagrun_pm_role', 'dagrun_pm_capacity',
  'dagrun_pm_defaulthoursperday', 'dagrun_pm_defaulthourlyrate',
  'dagrun_pm_dateadded', 'createdon',
];

// ── Pools ─────────────────────────────────────────────────────────

export async function listResourcePools(pca: IPublicClientApplication): Promise<ResourcePool[]> {
  return dvList<ResourcePool>(pca, 'dagrun_pm_resourcepools', {
    select: POOL_FIELDS,
    orderby: 'dagrun_pm_name asc',
    top: 200,
  });
}

/**
 * The tenant's global resource pool — one pool per tenant. Oldest pool wins
 * if legacy data ever holds more than one; the UI only ever creates one.
 */
export async function getTenantPool(pca: IPublicClientApplication): Promise<ResourcePool | null> {
  const pools = await dvList<ResourcePool>(pca, 'dagrun_pm_resourcepools', {
    select: POOL_FIELDS,
    orderby: 'createdon asc',
    top: 1,
  });
  return pools[0] ?? null;
}

export async function createResourcePool(pca: IPublicClientApplication, input: { name: string; description?: string | null; isActive?: boolean; ownerId?: string | null; ownerName?: string | null }): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_isactive: input.isActive ?? true,
  };
  if (input.description) body.dagrun_pm_description = input.description;
  if (input.ownerId)     body.dagrun_pm_ownerid = input.ownerId;
  if (input.ownerName)   body.dagrun_pm_ownername = input.ownerName;
  return dvCreate(pca, 'dagrun_pm_resourcepools', body);
}

export async function updateResourcePool(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_resourcepools', id, patch);
}

export async function deleteResourcePool(pca: IPublicClientApplication, id: string): Promise<void> {
  // Manual cascade of members to avoid orphan rows.
  try {
    const members = await dvList<ResourcePoolMember>(pca, 'dagrun_pm_resourcepoolmembers', {
      select: ['dagrun_pm_resourcepoolmemberid'],
      filter: `dagrun_pm_resourcepoolid eq '${id}'`,
      top: 500,
    });
    for (const m of members) {
      try { await dvDelete(pca, 'dagrun_pm_resourcepoolmembers', m.dagrun_pm_resourcepoolmemberid); }
      catch { /* best effort */ }
    }
  } catch { /* ignore */ }
  return dvDelete(pca, 'dagrun_pm_resourcepools', id);
}

// ── Members ───────────────────────────────────────────────────────

export async function listPoolMembers(pca: IPublicClientApplication, poolId: string): Promise<ResourcePoolMember[]> {
  return dvList<ResourcePoolMember>(pca, 'dagrun_pm_resourcepoolmembers', {
    select: MEMBER_FIELDS,
    filter: `dagrun_pm_resourcepoolid eq '${poolId}'`,
    orderby: 'dagrun_pm_username asc',
    top: 500,
  });
}

export interface CreateMemberInput {
  poolId: string;
  userId: string;
  userName: string;
  userEmail?: string | null;
  role?: string | null;
  capacity?: number | null; // 0.0..1.0 FTE
  hoursPerDay?: number | null;  // defaults to DEFAULT_HOURS_PER_DAY
  hourlyRate?: number | null;   // PM-only surface; nullable
}

export async function createPoolMember(pca: IPublicClientApplication, input: CreateMemberInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.userName,
    dagrun_pm_resourcepoolid: input.poolId,
    dagrun_pm_userid: input.userId,
    dagrun_pm_username: input.userName,
    dagrun_pm_defaulthoursperday: input.hoursPerDay ?? DEFAULT_HOURS_PER_DAY,
    dagrun_pm_dateadded: new Date().toISOString().slice(0, 10),
  };
  if (input.userEmail)        body.dagrun_pm_useremail = input.userEmail;
  if (input.role)             body.dagrun_pm_role = input.role;
  if (input.capacity != null) body.dagrun_pm_capacity = input.capacity;
  if (input.hourlyRate != null) body.dagrun_pm_defaulthourlyrate = input.hourlyRate;
  return dvCreate(pca, 'dagrun_pm_resourcepoolmembers', body);
}

/**
 * Active project tasks currently assigned to a user — the remove-member
 * warning. "Active" = any status except Done and Cancelled.
 */
export async function countActiveAssignedTasks(
  pca: IPublicClientApplication,
  userId: string,
): Promise<number> {
  const rows = await dvList<{ dagrun_pm_taskid: string }>(pca, 'dagrun_pm_tasks', {
    select: ['dagrun_pm_taskid'],
    filter: `dagrun_pm_assigneeid eq '${userId}' and dagrun_pm_status ne 100000003 and dagrun_pm_status ne 100000005`,
    top: 500,
  });
  return rows.length;
}

export async function updatePoolMember(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_resourcepoolmembers', id, patch);
}

export async function deletePoolMember(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_resourcepoolmembers', id);
}
